import { GenNode } from '../types/carbon/models/GenNode'
import { MetaData } from '../types/carbon/models/MetaData'
import {
  MultiTable,
} from '../types/gotypes'
import { flattenTree } from './tree'
export const MULTI_TABLE_FOLDER_META_KEY = 'synthetic'
export const MULTI_TABLE_FOLDER_META_FOLDER_VALUE = 'multiTablesFolder'
export const MULTI_TABLE_NODE_META_VALUE = 'multiTable'
export const MULTI_TABLE_ID_META_KEY = 'multiTableId'
// CBT metadata keys - these are injected into TOC nodes by the server
export const CBT_DESCRIPTION_META_KEY = 'cbtDescription'
export const CBT_SIDE_META_KEY = 'cbtSide'

export const cloneNodeDeep = (node: GenNode): GenNode => ({
  ...node,
  meta: node.meta ? node.meta.map<MetaData>((item) => ({ ...item })) : undefined,
  children: node.children ? node.children.map(child => cloneNodeDeep(child)) : undefined,
})

export const findFirstUserNode = (nodes: GenNode[]): GenNode | null => {
  for (const node of nodes) {
    if (node.type === 'User') {
      return node
    }
    if (node.children && node.children.length > 0) {
      const found = findFirstUserNode(node.children)
      if (found) return found
    }
  }
  return null
}

export const filterOutSyntheticChildren = (children: GenNode[] | null | undefined) => {
  if (!children) return []
  return children.filter(child => {
    const metaValue = child.meta?.find(item => item.key === MULTI_TABLE_FOLDER_META_KEY)?.value
    return metaValue !== MULTI_TABLE_FOLDER_META_FOLDER_VALUE
  })
}

export const buildMultiTableFolder = ({
  parentId,
  startingId,
  multiTables,
  showLoading,
}: {
  parentId: number | undefined
  startingId: number
  multiTables: MultiTable[]
  showLoading: boolean
}) => {
  let nextId = startingId
  const folderId = nextId++

  const folderChildren: GenNode[] = (() => {
    if (showLoading) {
      return [{
        type: 'Loading',
        id: nextId++,
        parentId: folderId,
        isExpanded: false,
        isFolder: false,
        value1: 'Loading',
        level: 0,
        anyChildren: false,
        children: [],
      }]
    }

    return multiTables.map(table => {
      const node: GenNode = {
        type: 'MultiTable',
        id: nextId++,
        parentId: folderId,
        isExpanded: false,
        isFolder: false,
        value1: table.config?.name || '',
        value2: 'multitables',
        level: 0,
        anyChildren: false,
        meta: [
          { key: MULTI_TABLE_FOLDER_META_KEY, value: MULTI_TABLE_NODE_META_VALUE },
          { key: MULTI_TABLE_ID_META_KEY, value: table.id },
        ],
        children: [],
      }
      return node
    })
  })()

  const folderNode: GenNode = {
    type: 'Folder',
    id: folderId,
    parentId: parentId ?? 0,
    isExpanded: true,
    isFolder: true,
    value1: 'My Table Lists',
    value2: 'multitables',
    level: 0,
    anyChildren: folderChildren.length > 0,
    meta: [{ key: MULTI_TABLE_FOLDER_META_KEY, value: MULTI_TABLE_FOLDER_META_FOLDER_VALUE }],
    children: folderChildren,
  }

  return {
    folderNode,
    nextId,
  }
}


/**
 * Normalizes a table path by ensuring consistent .cbt extension handling
 * @param path - The path to normalize
 * @param stripExtension - If true, removes .cbt; if false, ensures .cbt exists
 * @returns Normalized path
 */
export const normalizeTablePath = (path: string, stripExtension: boolean = true): string => {
  if (!path) return ''

  if (stripExtension) {
    // Remove .cbt extension if present
    return path.replace(/\.cbt$/i, '')
  } else {
    // Ensure .cbt extension exists
    return path.match(/\.cbt$/i) ? path : `${path}.cbt`
  }
}

/**
 * Compares two table paths for equality, accounting for .cbt extension differences
 * @param path1 - First path
 * @param path2 - Second path
 * @returns True if paths refer to the same table
 */
export const arePathsEqual = (path1: string, path2: string): boolean => {
  if (!path1 || !path2) return path1 === path2

  // Compare normalized paths (without extension)
  const normalized1 = normalizeTablePath(path1, true)
  const normalized2 = normalizeTablePath(path2, true)

  return normalized1 === normalized2
}

export const getAugementedTOC = ({
  toc,
  multiTables,
}: {
  toc: GenNode[],
  multiTables: MultiTable[],
}) => {
  const cloned = toc.map(node => cloneNodeDeep(node))
  if (cloned.length === 0) {
    return {
      nodes: cloned,
      multiTableFolderId: undefined as string | undefined,
      userNodeId: undefined as string | undefined,
    }
  }

  const userNode = findFirstUserNode(cloned)
  if (!userNode) {
    return {
      nodes: cloned,
      multiTableFolderId: undefined,
      userNodeId: undefined,
    }
  }

  const filteredChildren = filterOutSyntheticChildren(userNode.children)

  const allNodes = flattenTree(cloned)
  const maxId = allNodes.reduce((acc, node) => {
    if (typeof node.id === 'number') {
      return Math.max(acc, node.id)
    }
    return acc
  }, 0)

  const { folderNode } = buildMultiTableFolder({
    parentId: userNode.id,
    startingId: maxId + 1,
    multiTables,
    showLoading: false,
  })

  userNode.children = [...filteredChildren, folderNode]
  return {
    nodes: cloned,
    multiTableFolderId: folderNode.id?.toString(),
    userNodeId: userNode.id?.toString(),
  }
}